import { useQuery } from '@apollo/client';
import getConfig from 'next/config';
import { useCallback, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import useFormat from '../../hooks/useFormat';
import modulesConfig from '../../modules/modules';
import { NavigationItemType } from '../../types/module';
import { FETCH_WIDGETS, FetchWidgetsType } from '../api/widgets';
import DesktopHomepage from '../components/homepage/DesktopHomepage';
import MobileHomepage from '../components/homepage/MobileHomepage';
import { useSession } from '../components/session/SessionContext';
import useCustomRouter from '../hooks/useCustomRouter';
import { PublicRuntimeConfigType } from '../types/publicRuntimeConfig';

const Homepage = () => {
	const { isMobileFormat } = useFormat();
	const { customerFile: sessionCustomerFile, checkPermission } = useSession();
	const { publicRuntimeConfig } = getConfig() as PublicRuntimeConfigType;
	const { standaloneModule } = publicRuntimeConfig;
	const { replace, query } = useCustomRouter();

	const { data: widgetData, loading } = useQuery<FetchWidgetsType>(FETCH_WIDGETS, {
		skip: !!standaloneModule,
	});

	const { widgets } = widgetData ?? {};

	const widgetsWithKey = useMemo(
		() =>
			widgets?.flatMap((widget) =>
				(isMobileFormat && widget.isAvailableOnMobileFormat) || (!isMobileFormat && widget.isAvailableOnDesktopFormat)
					? {
							key: uuid(),
							widget,
					  }
					: [],
			),
		[isMobileFormat, widgets],
	);

	const navigateToRoute = useCallback(
		async ({ route, subItems }: Pick<NavigationItemType, 'route' | 'subItems'>) => {
			const { route: firstAllowedSubItemRoute, subItems: subSubItems } =
				subItems?.find(({ permissionCodes }) => permissionCodes?.some(checkPermission) ?? true) ?? {};

			const newPathnameArray = [...route.split('/'), firstAllowedSubItemRoute?.replace(/^\//, '')].filter(
				(item) => !!item && (standaloneModule ? item !== standaloneModule : true),
			);

			if (subSubItems && firstAllowedSubItemRoute) {
				await navigateToRoute({ route: `/${newPathnameArray.join('/')}`, subItems: subSubItems });

				return;
			}

			await replace({
				pathname: `/${newPathnameArray.join('/')}`,
				query,
			});
		},
		[checkPermission, query, replace, standaloneModule],
	);

	useEffect(() => {
		if (standaloneModule && !!sessionCustomerFile) {
			const foundStandaloneModule = modulesConfig.find(({ code }) => code === standaloneModule);

			if (foundStandaloneModule) {
				navigateToRoute({ route: foundStandaloneModule.route, subItems: foundStandaloneModule.subItems }).catch(
					(error) => {
						throw error;
					},
				);
			}
		}
	}, [sessionCustomerFile, standaloneModule, navigateToRoute]);

	return isMobileFormat ? (
		<MobileHomepage widgetsWithKey={widgetsWithKey} loading={loading} />
	) : (
		<DesktopHomepage widgetsWithKey={widgetsWithKey} loading={loading} />
	);
};

export default Homepage;
